import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, FileCode2 } from "lucide-react";
import { setSeo } from "@/lib/seo";
import { API } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";

const TITLE = "Fattura elettronica gratis: crea il file XML in un minuto | Operativo";
const META =
  "Crea la fattura elettronica in formato XML FatturaPA senza compilare i campi a mano: " +
  "descrivi il lavoro, l'AI scrive le voci e scarichi il file da caricare sull'Agenzia delle Entrate. " +
  "Corretto anche in regime forfettario. Prova gratuita di 7 giorni, senza carta.";
const PATH = "/fattura-elettronica-gratis";

const BULLETS = [
  "Voci, quantità, prezzi e IVA scritte dall'AI da una frase",
  "File XML in formato FatturaPA 1.2.2, quello ufficiale",
  "Natura e riferimento normativo corretti in regime forfettario",
  "Ti dice esattamente quale dato manca, prima di generare il file",
  "Stesso documento anche in PDF, col tuo logo e il tuo IBAN",
  "Numerazione progressiva automatica",
];

const STEPS = [
  {
    t: "1. Inserisci i tuoi dati una volta sola",
    d: "Regime fiscale, partita IVA, codice fiscale e sede. Li salvi in Impostazioni e non ci pensi più: finiscono in ogni fattura.",
  },
  {
    t: "2. Racconta il lavoro",
    d: "\"Rifacimento impianto elettrico appartamento 90 mq, 5 giorni\". L'AI compila le voci con quantità e prezzi, calcola IVA e totale.",
  },
  {
    t: "3. Scarica l'XML e trasmettilo",
    d: "Un clic e hai il file. Lo carichi su Fatture e Corrispettivi dell'Agenzia delle Entrate, oppure lo giri al commercialista già pronto.",
  },
];

const FAQ = [
  {
    q: "La fattura elettronica è obbligatoria anche in regime forfettario?",
    a: "Sì. Dal 1° gennaio 2024 l'obbligo riguarda tutti i titolari di partita IVA, compresi i forfettari, senza più soglie di ricavi. Operativo genera il file già corretto per il forfettario, con natura N2.2 e il riferimento alla Legge 190/2014.",
  },
  {
    q: "Cos'è il file XML FatturaPA?",
    a: "È il formato ufficiale con cui le fatture viaggiano attraverso il Sistema di Interscambio dell'Agenzia delle Entrate. Non è un PDF: è un file strutturato con codici precisi, ed è la parte che fa perdere più tempo se compilata a mano.",
  },
  {
    q: "Che dati mi servono per generarla?",
    a: "I tuoi: partita IVA, codice fiscale, regime fiscale e sede. Del cliente: denominazione, partita IVA o codice fiscale, indirizzo e codice destinatario SDI (oppure la sua PEC). Se manca qualcosa, Operativo te lo dice con precisione invece di produrre un file che verrebbe scartato.",
  },
  {
    q: "Cos'è il codice destinatario e dove lo trovo?",
    a: "È un codice di 7 caratteri che identifica il canale su cui il cliente riceve le fatture: lo comunica il cliente stesso, gliela chiedi una volta e la salvi nella sua scheda. Se non ce l'ha si usa la sua PEC.",
  },
  {
    q: "Operativo trasmette la fattura all'Agenzia delle Entrate?",
    a: "Non ancora: oggi generi e scarichi il file XML, e lo trasmetti tu dal portale Fatture e Corrispettivi o tramite il tuo commercialista. L'invio automatico al Sistema di Interscambio direttamente dall'app è in arrivo.",
  },
  {
    q: "È davvero gratis?",
    a: "La prova è di 7 giorni con tutto incluso, senza carta di credito. Dopo continui con la beta gratuita: preventivi, fatture e PDF illimitati e 30 azioni AI al mese.",
  },
];

// Pagina pubblica per chi cerca "fattura elettronica gratis" su Google.
const InvoiceXmlPage = () => {
  const { lang, setLang } = useI18n();

  useEffect(() => {
    if (lang !== "it") setLang("it");
  }, [lang, setLang]);

  useEffect(
    () =>
      setSeo({ title: TITLE, description: META, path: PATH, faq: FAQ, lang: "it",
               alternates: { it: PATH } }),
    []
  );

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="invoice-xml-page">
      <header className="flex items-center justify-between border-b border-slate-200 px-6 py-5 lg:px-16">
        <Link to="/" className="flex items-center gap-2 font-extrabold tracking-tight">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-900 text-sm text-white">
            O
          </span>
          Operativo
        </Link>
        <Link to="/registrati" data-testid="invoice-header-cta">
          <Button className="bg-slate-900 text-white hover:bg-slate-800">Prova gratis</Button>
        </Link>
      </header>

      <section className="grid items-center gap-14 px-6 py-16 lg:grid-cols-[1.05fr_1fr] lg:px-16">
        <div className="max-w-2xl">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-emerald-700">
            <FileCode2 className="h-3 w-3" /> Formato FatturaPA 1.2.2
          </span>
          <h1 className="mt-5 text-4xl font-extrabold tracking-tight sm:text-5xl">
            Fattura elettronica senza compilare i campi a mano
          </h1>
          <p className="mt-6 text-base leading-relaxed text-slate-600 md:text-lg">
            Descrivi il lavoro con le tue parole: Operativo scrive le voci, calcola IVA e totale e ti
            consegna il file <b>XML</b> pronto da trasmettere, più lo stesso documento in PDF col tuo
            logo. Corretto anche in regime forfettario.
          </p>
          <ul className="mt-8 grid gap-3 sm:grid-cols-2">
            {BULLETS.map((b) => (
              <li key={b} className="flex items-start gap-2 text-sm text-slate-700">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                {b}
              </li>
            ))}
          </ul>
          <Link to="/registrati" data-testid="invoice-hero-cta">
            <Button className="mt-9 gap-2 bg-slate-900 px-6 py-6 text-base text-white hover:bg-slate-800">
              Inizia la prova gratuita di 7 giorni <ArrowRight className="h-4 w-4" />
            </Button>
          </Link>
          <p className="mt-3 text-xs text-slate-500">
            Nessuna carta richiesta · Poi continui con la beta gratuita
          </p>
        </div>
        <figure className="lg:justify-self-end">
          <img
            src={`${API}/media/demo-it.jpg`}
            alt="Documento generato da Operativo con voci, quantità, IVA e totale"
            className="w-full rounded-xl border border-slate-200 shadow-[0_30px_60px_-30px_rgba(15,23,42,0.35)]"
            width="1280"
            height="720"
            loading="lazy"
          />
          <figcaption className="mt-3 text-xs text-slate-500">
            Un documento generato da una riga di testo: lo stesso motore scrive preventivi e
            fatture. Dalla fattura escono il PDF e il file XML.
          </figcaption>
        </figure>
      </section>

      <section className="border-y border-slate-200 bg-slate-50 px-6 py-20 lg:px-16">
        <h2 className="text-2xl font-extrabold tracking-tight lg:text-3xl">
          Come si fa, in tre passaggi
        </h2>
        <div className="mt-10 grid gap-10 md:grid-cols-3">
          {STEPS.map((s) => (
            <div key={s.t}>
              <h3 className="text-base font-semibold md:text-lg">{s.t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="px-6 py-20 lg:px-16">
        <h2 className="text-2xl font-extrabold tracking-tight lg:text-3xl">Domande frequenti</h2>
        <div className="mt-10 grid max-w-4xl gap-8 md:grid-cols-2">
          {FAQ.map(({ q, a }) => (
            <div key={q}>
              <h3 className="text-base font-semibold">{q}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-slate-950 px-6 py-20 text-white lg:px-16">
        <div className="max-w-2xl">
          <h2 className="text-2xl font-extrabold tracking-tight lg:text-3xl">
            Le fatture sono la parte noiosa. Falla fare a lui.
          </h2>
          <p className="mt-4 text-sm text-slate-400 md:text-base">
            Preventivo accettato? Diventa fattura con un clic, con numerazione, PDF e XML. E se il
            cliente non paga, il sollecito lo scrive Operativo.
          </p>
          <Link to="/registrati" data-testid="invoice-footer-cta">
            <Button className="mt-8 gap-2 bg-white px-6 py-6 text-base text-slate-900 hover:bg-slate-200">
              Inizia gratis <ArrowRight className="h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>

      <footer className="flex flex-wrap items-center gap-x-6 gap-y-2 px-6 py-10 text-sm text-slate-500 lg:px-16">
        <Link to="/" className="hover:text-slate-900">
          Torna alla home
        </Link>
        <Link to="/generatore-preventivi-gratis" className="hover:text-slate-900">
          Generatore di preventivi gratis
        </Link>
        <Link to="/privacy" className="hover:text-slate-900">
          Privacy
        </Link>
      </footer>
    </div>
  );
};

export default InvoiceXmlPage;
